import React, { useCallback, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import {
    DBlock,
    DBlockHeader,
    DDatePicker,
    DPage,
    DPageBody,
    DTabs,
} from '../../../dum';
import {
    useProductionPerformanceMachinesQuery,
    useProductionPerformanceProductsWithRunsQuery,
} from '../../../../services';
import { DATE_FORMAT } from '../../../../helpers';
import { HOURLY_DATA_EPOCH } from '../../../smart/production-performance';
import { MachinesTab } from './tabs/machines-tab';
import { ProductsTab } from './tabs/products-tab';
import { WeeklyAuditTab } from './tabs/weekly-audit-tab';
import {
    PerfFiltersProvider,
    PerfFocusProvider,
    PerfRunFocus,
    PerfRunFocusRequest,
} from './perf-filters-context';

export function ProductionPerformance() {
    const [fromDate, setFromDate] = useState<string>(HOURLY_DATA_EPOCH);
    const [toDate, setToDate] = useState<string | null>(null);
    // Restricts the PACKED figures to single-product runs, whose per-product
    // hours are clean: the analysis panel's Producción kg/hr + scatter, and the
    // weekly audit (which drops multi-product corridas and grades against the
    // single-product baseline). Consumo kg/hr stays over all runs.
    const [singleProductOnly, setSingleProductOnly] = useState(true);

    const { data } = useProductionPerformanceMachinesQuery();
    const { data: productsData } =
        useProductionPerformanceProductsWithRunsQuery();
    const machines = useMemo(() => {
        const typeNames = new Map(
            (data?.getOrderProductionTypes ?? []).map((type) => [
                type.id,
                type.name,
            ]),
        );
        return (data?.getMachines ?? [])
            .map((machine) => ({
                ...machine,
                categoryName:
                    typeNames.get(machine.order_production_type_id ?? 0) ??
                    'Sin tipo de producción',
            }))
            .sort((a, b) =>
                a.categoryName.localeCompare(b.categoryName, 'es', {
                    sensitivity: 'base',
                }) ||
                a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
            );
    }, [data]);
    const products = useMemo(
        () =>
            (productsData?.getProductsWithRuns ?? [])
                .map((product) => ({
                    ...product,
                    categoryName:
                        product.product_category_name ?? 'Sin categoría',
                }))
                .sort((a, b) =>
                    a.categoryName.localeCompare(b.categoryName, 'es', {
                        sensitivity: 'base',
                    }) ||
                    a.description.localeCompare(b.description, 'es', {
                        sensitivity: 'base',
                    }),
                ),
        [productsData],
    );

    // The filters ride in context (see perf-filters-context), so these tab
    // content closures depend ONLY on the selector catalogues. That keeps their identity
    // stable across date/checkbox changes — DTabs re-renders the tab in place
    // instead of remounting it and losing the selected machine/product.
    const filters = useMemo(
        () => ({ fromDate, toDate, singleProductOnly }),
        [fromDate, toDate, singleProductOnly],
    );

    // Corrida focus for the Máquinas/Productos analysis scatter (click a point →
    // highlight it). Page-level because the scatter that emits it and the panel
    // that reads it are separate nodes.
    const [runFocus, setRunFocus] = useState<PerfRunFocus | null>(null);
    const focusRun = useCallback((run: PerfRunFocusRequest) => {
        setRunFocus((current) => ({
            ...run,
            requestId: (current?.requestId ?? 0) + 1,
        }));
    }, []);
    const focusValue = useMemo(
        () => ({ runFocus, focusRun }),
        [runFocus, focusRun],
    );
    // Order production types + branches for the weekly audit's filters.
    const types = useMemo(
        () => data?.getOrderProductionTypes ?? [],
        [data],
    );
    const branches = useMemo(() => data?.getBranches ?? [], [data]);
    const tabs = useMemo(
        () => [
            {
                title: 'Auditoría semanal',
                content: () => (
                    <WeeklyAuditTab types={types} branches={branches} />
                ),
            },
            {
                title: 'Máquinas',
                content: () => (
                    <MachinesTab machines={machines} products={products} />
                ),
            },
            {
                title: 'Productos',
                content: () => (
                    <ProductsTab machines={machines} products={products} />
                ),
            },
        ],
        [machines, products, types, branches],
    );

    return (
        <DPage>
            <DPageBody disableGutters>
                <DBlock>
                    <DBlockHeader>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                            }}
                        >
                            <Box sx={{ width: 180 }}>
                                <DDatePicker
                                    views={['day']}
                                    inputFormat={DATE_FORMAT}
                                    label={'Desde'}
                                    value={fromDate}
                                    clearable={false}
                                    disableGutters
                                    onChange={(val) => {
                                        if (val) setFromDate(val);
                                    }}
                                />
                            </Box>
                            <Box sx={{ width: 180 }}>
                                <DDatePicker
                                    views={['day']}
                                    inputFormat={DATE_FORMAT}
                                    label={'Hasta'}
                                    value={toDate ?? ''}
                                    clearable
                                    disableGutters
                                    onChange={(val) => setToDate(val ?? null)}
                                />
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size={'small'}
                                        checked={singleProductOnly}
                                        onChange={(e) =>
                                            setSingleProductOnly(
                                                e.target.checked,
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography variant={'caption'}>
                                        Solo producciones de un solo producto
                                    </Typography>
                                }
                            />
                        </Box>
                    </DBlockHeader>

                    <PerfFiltersProvider value={filters}>
                        <PerfFocusProvider value={focusValue}>
                            {/* Flex column so DTabs' body fills this box exactly.
                                The active tab then owns its scroll — the weekly
                                audit table scrolls internally (sticky header),
                                the Máquinas/Productos tabs scroll in the tab body
                                — so the page never shows a second scrollbar. */}
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflowY: 'auto',
                                }}
                            >
                                <DTabs items={tabs} />
                            </Box>
                        </PerfFocusProvider>
                    </PerfFiltersProvider>
                </DBlock>
            </DPageBody>
        </DPage>
    );
}
